import React, { useEffect, useRef } from 'react';
import { cn } from '../lib/utils';

/** Frames are sampled at this width and upscaled by CSS, which softens them for free. */
const SAMPLE_WIDTH_PX = 480;

/** The backdrop is decorative, so it samples well below the feed's frame rate. */
const SAMPLE_INTERVAL_MS = 100;

/** Follows the Tailwind `dark` class of the host app. */
const DEFAULT_BAR_CLASS_NAME = 'bg-white dark:bg-black';

/** A letterbox bar, in container-relative pixels. */
export interface VideoBackdropBar {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Copy frames from a playing feed into a canvas at a low resolution and frame rate.
 *
 * @param video The element playing the feed, or null while no player has mounted one.
 * @param enabled Whether there is any backdrop on screen to draw into.
 * @returns A ref to attach to the canvas that receives the frames.
 */
function useSampledFrames(video: HTMLVideoElement | null, enabled: boolean) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!video || !canvas || !context || !enabled) return;

    const draw = () => {
      const { videoWidth, videoHeight } = video;
      if (videoWidth <= 0 || videoHeight <= 0) return;

      // The backing store keeps the camera's aspect ratio so `object-cover` can crop it the same
      // way it would crop the feed itself.
      const height = Math.max(1, Math.round((SAMPLE_WIDTH_PX * videoHeight) / videoWidth));
      if (canvas.width !== SAMPLE_WIDTH_PX || canvas.height !== height) {
        canvas.width = SAMPLE_WIDTH_PX;
        canvas.height = height;
      }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    };

    // Driven by `requestAnimationFrame` rather than a timer so sampling stops while the tab is in
    // the background.
    let frame = 0;
    let lastDrawnAt = Number.NEGATIVE_INFINITY;
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      if (now - lastDrawnAt < SAMPLE_INTERVAL_MS) return;
      lastDrawnAt = now;
      draw();
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [video, enabled]);

  return canvasRef;
}

export interface VideoBackdropProps {
  /**
   * The empty regions flanking the fitted video, in container-relative pixels. They arrive as
   * explicit rectangles rather than as flex space so they can be painted beneath the feed.
   */
  bars: VideoBackdropBar[];
  /** The element playing the visible feed, whose frames fill the bars. */
  videoElement: HTMLVideoElement | null;
  /** Darkens the bars with a scrim, e.g. to match an error overlay on the feed. */
  dimmed?: boolean;
  /**
   * Replaces the default bar colour (`bg-white dark:bg-black`) instead of merging with it, so
   * include any `dark:` class the colour needs. The blurred copy fades towards this colour, so it
   * sets how strongly the copy reads.
   */
  barClassName?: string;
  /** Prefix for the `data-testid` of each part: `-bar`, `-backdrop` and `-bar-scrim`. */
  testIdPrefix?: string;
}

/**
 * Fills the letterbox bars around a video with a dimmed, blurred copy of that same video, so the
 * player reads as a solid block without competing with the video itself. The copy is zoomed in to
 * cover the whole container, so the bars continue the colours at the edges of the frame. The
 * fitted video shows the whole frame and is never cropped.
 *
 * Render it behind the video, inside a `relative` container that the bars are measured against.
 */
export const VideoBackdrop = React.memo<VideoBackdropProps>(
  ({ bars, videoElement, dimmed = false, barClassName, testIdPrefix = 'video-backdrop' }) => {
    const hasBars = bars.length > 0;
    const canvasRef = useSampledFrames(videoElement, hasBars);

    return (
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {bars.map((bar) => (
          <div
            key={`${bar.left}-${bar.top}`}
            data-testid={`${testIdPrefix}-bar`}
            className={cn('absolute', barClassName ?? DEFAULT_BAR_CLASS_NAME)}
            style={{ left: bar.left, top: bar.top, width: bar.width, height: bar.height }}
          />
        ))}
        {hasBars && (
          <canvas
            ref={canvasRef}
            data-testid={`${testIdPrefix}-backdrop`}
            // The opacity fades the copy towards whichever colour the bars are painted in, and the
            // slight scale keeps the blur's soft edge outside the clipped container.
            className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-sm"
          />
        )}
        {dimmed &&
          bars.map((bar) => (
            <div
              key={`${bar.left}-${bar.top}`}
              data-testid={`${testIdPrefix}-bar-scrim`}
              className="absolute bg-black/70"
              style={{ left: bar.left, top: bar.top, width: bar.width, height: bar.height }}
            />
          ))}
      </div>
    );
  },
);
VideoBackdrop.displayName = 'VideoBackdrop';
